const IST_LOCALE = 'en-IN';
const IST_TZ = 'Asia/Kolkata';

/**
 * Indian Rupee formatting: ₹1,23,456 (lakh-crore comma grouping).
 * Locked to en-IN so we always render Indian style regardless of UI language.
 */
export function formatINR(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat(IST_LOCALE, {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatINRWithPaise(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat(IST_LOCALE, {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a Date/ISO string in IST, 24-hour clock. Dates render dd/mm/yyyy.
 * Examples:
 *   formatISTTime('2026-05-16T09:30:00Z')  → '15:00'
 *   formatISTDate('2026-05-16T09:30:00Z')  → '16/05/2026'
 *   formatISTDateTime(...)                  → '16/05/2026, 15:00'
 */
export function formatISTTime(d: Date | string | null | undefined): string {
  if (!d) return '—';
  return new Intl.DateTimeFormat(IST_LOCALE, {
    timeZone: IST_TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(typeof d === 'string' ? new Date(d) : d);
}

export function formatISTDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  // en-GB → dd/mm/yyyy regardless of the device locale.
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(typeof d === 'string' ? new Date(d) : d);
}

export function formatISTDateTime(d: Date | string | null | undefined): string {
  if (!d) return '—';
  // en-GB → dd/mm/yyyy, HH:MM (24h).
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(typeof d === 'string' ? new Date(d) : d);
}

/**
 * IST calendar date as YYYY-MM-DD — for `<input type="date">` min/max bounds.
 * en-CA yields ISO ordering; Asia/Kolkata pins it to IST regardless of device
 * timezone. Returns '' for null/invalid.
 */
export function istDateYmd(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

/**
 * Days from now until target date. Positive = in future, negative = past.
 * Returns null if date is null/invalid.
 */
export function daysUntil(d: Date | string | null | undefined): number | null {
  if (!d) return null;
  const target = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(target.getTime())) return null;
  return Math.floor((target.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}
